import React, { useState, useRef } from 'react';
import { Button } from 'system/atoms';
import { Stack, SxProps, Theme, CircularProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  FieldProps,
  ServerDropdownFieldSchema,
  FormOption
} from '../types/Form';
import { FieldWrapper } from './FieldWrapper';
import { getNewOptionValues } from 'common/form/utils/getNewOptionValues';
import { useServerSearchOptions } from '../hooks/useServerSearchOptions';
import { DropdownList } from 'system';
import { SubLabelWithClearLink } from './SubLabelWithClearLink';

const dropdownButtonSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  justifyContent: 'space-between',
  fontWeight: 'normal',
  borderRadius: '8px'
};

export const ServerDropdown: React.FC<
  FieldProps<ServerDropdownFieldSchema>
> = ({ name, value, field, errorMessage, error, onChange }) => {
  const {
    label,
    subLabel,
    containerSx,
    stickyButtonEndComponent,
    searchPlaceholder,
    isMulti = false,
    buttonText,
    isClearable = false,
    clearLabel,
    popperSx,
    onSearchFilter,
    fetchOnMount,
    minSearchLength,
    listMaxHeight,
    initialSelectedOption,
    endAdornment,
    isLoading: isLoadingOverride
  } = field;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Remembers the options the user has selected so the button keeps showing
  // their labels after a later search/refetch replaces `options` — otherwise a
  // searched-for selection reverts to the placeholder once the list changes.
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<
    Record<string, FormOption>
  >({});
  const anchorRef = useRef<HTMLButtonElement>(null);

  const { options, isLoading } = useServerSearchOptions({
    isOpen,
    searchQuery,
    onSearchFilter,
    fetchOnMount,
    minSearchLength
  });

  // Show a loading spinner on the button (without disabling it) while options
  // load in the background or a preselected value is being resolved.
  const showButtonLoading =
    (isLoading && !isOpen) || Boolean(isLoadingOverride);

  // Convert value to array for multi-select
  const selectedValues = Array.isArray(value) ? value : [value];

  // Resolve selected options from the current list first, falling back to the
  // cache (so a selection stays labelled even when it's no longer in the current
  // searched/refetched `options`), then to `initialSelectedOption` (a preselected
  // value whose option may never appear in the fetched results).
  const selectedOptions = selectedValues
    .map(
      (val) =>
        options.find((opt) => opt.value === val) ??
        selectedOptionsCache[val] ??
        (initialSelectedOption?.value === val
          ? initialSelectedOption
          : undefined)
    )
    .filter((opt): opt is FormOption => Boolean(opt));

  const handleSelect = (optionValue: string) => {
    const chosenOption = options.find((opt) => opt.value === optionValue);
    if (chosenOption) {
      setSelectedOptionsCache((prev) => ({
        ...prev,
        [optionValue]: chosenOption
      }));
    }

    const newOptionValues = getNewOptionValues(
      optionValue,
      selectedValues,
      isMulti
    );
    if (!isMulti) {
      onChange(newOptionValues);
      setIsOpen(false);
      setSearchQuery('');
    } else {
      onChange(newOptionValues);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // A single selection may carry an `endItem` (e.g. a status pill) to sit beside its label
  // on the button, so the chosen value can show more than a plain string.
  const getButtonContent = () => {
    if (!isMulti) {
      const selected = selectedOptions[0];

      if (!selected) {
        return buttonText ?? 'Choose';
      }

      if (!selected.endItem) {
        return selected.label;
      }

      return (
        <Stack direction="row" alignItems="center" gap="16px">
          {selected.label}
          {selected.endItem}
        </Stack>
      );
    }

    if (selectedOptions.length === 0) {
      return buttonText ?? 'Choose';
    }

    return `Choose (${selectedOptions.length})`;
  };

  return (
    <FieldWrapper
      label={label}
      subLabel={
        <SubLabelWithClearLink
          subLabel={subLabel}
          isClearable={isClearable}
          onClearClicked={() => onChange([])}
          clearLabel={clearLabel}
        />
      }
      errorMessage={errorMessage}
      sx={containerSx}
    >
      <Stack direction="row" alignItems="center" gap="20px">
        <Button
          ref={anchorRef}
          data-testid={`server-dropdown-${name}`}
          fullWidth
          variant="secondary"
          type="button"
          state={errorMessage || error ? 'destructive' : 'hover'}
          endIcon={
            showButtonLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <KeyboardArrowDownIcon />
            )
          }
          sx={dropdownButtonSx}
          onClick={() => setIsOpen(!isOpen)}
        >
          {getButtonContent()}
        </Button>
        {endAdornment}
      </Stack>

      <DropdownList
        popperProps={{
          open: isOpen,
          anchorEl: anchorRef.current,
          placement: 'bottom-start',
          style: { width: anchorRef.current?.offsetWidth },
          sx: popperSx
        }}
        options={options}
        searchOptions={{
          placeholder: searchPlaceholder,
          searchQuery,
          onChange: setSearchQuery,
          minSearchLength
        }}
        selectedValues={selectedValues}
        isLoading={isLoading}
        maxHeight={listMaxHeight}
        stickyButton={
          stickyButtonEndComponent && {
            text: stickyButtonEndComponent.text,
            startIcon: stickyButtonEndComponent.startIcon,
            onClick: () => {
              handleClose();
              stickyButtonEndComponent.onClick();
            }
          }
        }
        onSelect={handleSelect}
        onClickAway={handleClose}
      />
    </FieldWrapper>
  );
};
